import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { adminApi, API, orderPdfUrl, downloadBlob } from "../../lib/api";
import { eur, STATUS_LABELS } from "../../lib/format";
import { toast } from "sonner";
import { ArrowLeft, Download, CheckCircle, FileText, RotateCcw, Pencil, Trash2, Mail, Paperclip, Send, RefreshCw } from "lucide-react";

const EMAIL_STATUS = {
  enviado: { label: "Enviado", cls: "bg-emerald-100 text-emerald-700" },
  enviado_sin_adjunto: { label: "Enviado sin PDF", cls: "bg-amber-100 text-amber-700" },
  fallido: { label: "Fallido", cls: "bg-red-100 text-red-700" },
};

const EmailHistory = ({ orderId, refreshKey }) => {
  const [logs, setLogs] = useState([]);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => adminApi.get(`/admin/orders/${orderId}/emails`).then((r) => setLogs(r.data)), 1500);
    return () => clearTimeout(t);
  }, [orderId, refreshKey, tick]);
  const resend = async (l) => {
    try {
      await adminApi.post(`/admin/emails/${l.id}/resend`);
      toast.success(`Reenviando a ${l.to}…`);
      setTimeout(() => setTick((x) => x + 1), 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo reenviar");
    }
  };
  return (
    <div className="bg-white border border-border rounded-sm p-4 text-sm" data-testid="order-email-history">
      <p className="font-heading font-semibold mb-2 flex items-center gap-2"><Mail className="h-4 w-4" /> Emails enviados</p>
      {logs.length === 0 ? <p className="text-xs text-muted-foreground">Sin emails registrados para este pedido.</p> : (
        <ul className="space-y-2">
          {logs.map((l) => {
            const s = EMAIL_STATUS[l.status] || { label: l.status, cls: "bg-secondary" };
            return (
              <li key={l.id} className="border-t border-border pt-2 first:border-0 first:pt-0" data-testid={`email-log-${l.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <span className="text-xs break-all">{l.to}</span>
                  <span className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-sm ${s.cls}`}>{s.label}</span>
                </div>
                <p className="text-xs text-muted-foreground">{l.subject}</p>
                <p className="text-[10px] text-muted-foreground flex items-center gap-1">{new Date(l.sent_at).toLocaleString("es-ES")}{l.attachment && <><Paperclip className="h-3 w-3" />{l.attachment}</>}</p>
                {l.error && <p className="text-[10px] text-red-600 break-words">{l.error}</p>}
                {l.status !== "enviado" && <button type="button" onClick={() => resend(l)} className="mt-1 inline-flex items-center gap-1 text-[11px] text-brand hover:underline" data-testid={`resend-email-${l.id}`}><RefreshCw className="h-3 w-3" /> Reenviar</button>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default function AdminOrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [o, setO] = useState(null);

  const load = useCallback(() => adminApi.get(`/admin/orders/${id}`).then((r) => setO(r.data)), [id]);
  useEffect(() => { load(); }, [load]);

  if (!o) return <div className="h-40 bg-secondary animate-pulse rounded-sm" />;

  const deleteOrder = async () => {
    if (!window.confirm(`¿Eliminar el pedido ${o.number}? Esta acción no se puede deshacer.`)) return;
    await adminApi.delete(`/admin/orders/${id}`);
    toast.success("Pedido eliminado");
    nav("/admin/pedidos");
  };

  const changeStatus = async (status) => {
    await adminApi.put(`/admin/orders/${id}`, { status });
    toast.success("Estado actualizado");
    load();
  };
  const saveObs = async (observations) => {
    await adminApi.put(`/admin/orders/${id}`, { observations });
    toast.success("Observaciones guardadas");
  };
  const confirmPayment = async () => {
    await adminApi.post(`/admin/orders/${id}/confirm-payment`);
    toast.success("Pago confirmado");
    load();
  };
  const sendPayLink = async () => {
    try {
      const r = await adminApi.post(`/admin/orders/${id}/send-pay-link`);
      toast.success(`Enlace de pago enviado a ${r.data.to}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo enviar el enlace");
    }
  };
  const genInvoice = async () => {
    const r = await adminApi.post(`/admin/orders/${id}/invoice`);
    toast.success(`Factura ${r.data.number} generada`);
    load();
  };
  const createReturn = async () => {
    const reason = window.prompt("Motivo de la devolución/incidencia:");
    if (!reason) return;
    await adminApi.post("/admin/returns", { order_id: id, reason, kind: "devolucion" });
    toast.success("Devolución registrada");
  };

  return (
    <div className="max-w-4xl">
      <button onClick={() => nav("/admin/pedidos")} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-brand mb-4"><ArrowLeft className="h-4 w-4" /> Volver a pedidos</button>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tighter">Pedido {o.number}</h1>
          <p className="text-muted-foreground text-sm">{o.date}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={orderPdfUrl(o.id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-border rounded-sm px-3 py-2 text-sm hover:border-brand" data-testid="order-pdf-btn"><Download className="h-4 w-4" /> PDF pedido</a>
          <button onClick={() => nav(`/admin/pedidos/${o.id}/editar`)} className="inline-flex items-center gap-2 border border-border rounded-sm px-3 py-2 text-sm hover:border-brand" data-testid="edit-order-btn"><Pencil className="h-4 w-4" /> Editar</button>
          <button onClick={confirmPayment} className="inline-flex items-center gap-2 bg-emerald-600 text-white rounded-sm px-3 py-2 text-sm hover:bg-emerald-700" data-testid="confirm-payment-btn"><CheckCircle className="h-4 w-4" /> Confirmar pago</button>
          {!o.paid && o.status !== "pago_recibido" && (
            <button onClick={sendPayLink} className="inline-flex items-center gap-2 bg-amber-500 text-white rounded-sm px-3 py-2 text-sm hover:bg-amber-600" data-testid="send-pay-link-btn"><Send className="h-4 w-4" /> Enviar enlace de pago</button>
          )}
          {o.invoice_id ? (
            <button type="button" onClick={() => downloadBlob(`/admin/invoices/${o.invoice_id}/pdf`, `factura-${o.number}.pdf`)} className="inline-flex items-center gap-2 bg-brand text-white rounded-sm px-3 py-2 text-sm hover:bg-brand-dark" data-testid="order-invoice-pdf-btn"><FileText className="h-4 w-4" /> Descargar factura</button>
          ) : (
            <button onClick={genInvoice} className="inline-flex items-center gap-2 bg-brand text-white rounded-sm px-3 py-2 text-sm hover:bg-brand-dark" data-testid="generate-invoice-btn"><FileText className="h-4 w-4" /> Generar factura</button>
          )}
          <button onClick={createReturn} className="inline-flex items-center gap-2 border border-border rounded-sm px-3 py-2 text-sm hover:border-brand"><RotateCcw className="h-4 w-4" /> Devolución</button>
          <button onClick={deleteOrder} className="inline-flex items-center gap-2 border border-red-300 text-red-600 rounded-sm px-3 py-2 text-sm hover:bg-red-50" data-testid="delete-order-btn"><Trash2 className="h-4 w-4" /> Eliminar</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white border border-border rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary"><tr><th className="text-left px-3 py-2">Producto</th><th className="text-center px-3 py-2">Cant.</th><th className="text-right px-3 py-2">P.Unit</th><th className="text-right px-3 py-2">Subtotal</th></tr></thead>
              <tbody>
                {o.items.map((it, i) => (
                  <tr key={i} className="border-t border-border"><td className="px-3 py-2">{it.name}<span className="block text-xs font-mono text-muted-foreground">{it.reference}</span></td><td className="text-center">{it.quantity}</td><td className="text-right">{eur(it.price)}</td><td className="text-right font-medium">{eur(it.price * it.quantity)}</td></tr>
                ))}
              </tbody>
            </table>
            <div className="p-3 border-t border-border text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Base</span><span className="font-mono">{eur(o.subtotal)}</span></div>
              {o.shipping !== undefined && <div className="flex justify-between" data-testid="order-shipping"><span className="text-muted-foreground">Portes{o.shipping_label ? <span className="block text-[10px]">{o.shipping_label}</span> : null}</span><span className={`font-mono ${o.shipping_status === "consultar" ? "text-amber-700" : ""}`}>{o.shipping_status === "consultar" ? "a consultar" : eur(o.shipping || 0)}</span></div>}
              <div className="flex justify-between"><span className="text-muted-foreground">IVA</span><span className="font-mono">{eur(o.iva)}</span></div>
              <div className="flex justify-between font-bold"><span>Total</span><span className="text-brand">{eur(o.total)}</span></div>
            </div>
          </div>
          <div className="bg-white border border-border rounded-sm p-4">
            <label className="text-sm font-medium block mb-1">Observaciones</label>
            <textarea defaultValue={o.observations} rows={3} onBlur={(e) => saveObs(e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm" data-testid="order-observations" />
          </div>
        </div>

        <div className="space-y-5">
          <div className="bg-white border border-border rounded-sm p-4">
            <label className="text-sm font-medium block mb-2">Estado del pedido</label>
            <select value={o.status} onChange={(e) => changeStatus(e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm bg-white" data-testid="order-status-select">
              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="bg-white border border-border rounded-sm p-4 text-sm space-y-1">
            <p className="font-heading font-semibold mb-2">Cliente</p>
            <p>{o.customer_name}</p>
            <p className="text-muted-foreground">{o.company}</p>
            <p className="text-muted-foreground font-mono text-xs">{o.cif}</p>
            <p className="text-muted-foreground">{o.email}</p>
            <p className="text-muted-foreground">{o.phone}</p>
            <p className="text-muted-foreground pt-2">{o.address}, {o.postal_code} {o.city} ({o.province})</p>
          </div>
          <EmailHistory orderId={id} refreshKey={o.status + (o.invoice_id || "")} />
        </div>
      </div>
    </div>
  );
}
